import axios, { AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import { showApiError } from './vueSweetalert2';
import { getBaseUrl } from './getBaseUrl';

type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';

interface ApiCallParams {
    method: HttpMethod;
    url: string;
    data?: Record<string, any> | FormData;
    params?: Record<string, any>;
    headers?: Record<string, string> | null;
    responseType?: AxiosRequestConfig['responseType'];
    apiVersion?: string; // 'v1' | 'v2'
    showAlert?: boolean;
    responseError?: boolean;
}

export function apiCall({ 
    method, 
    url, 
    data = {}, 
    params = {}, 
    headers = null, 
    responseType = 'json', 
    apiVersion = 'v2', 
    showAlert = true 
}: ApiCallParams): {
    request: Promise<any>;
    cancel: () => void;
} {
    // Definición del apikey
    const apikey = localStorage.getItem('apikey');
    const cancelToken = axios.CancelToken;
    const source: CancelTokenSource = cancelToken.source();
    
    // Configurar headers con API key
    const axiosConfigHeaders = {
        'X-API-KEY': apikey || ''
    };
    
    if (headers == null) {
        headers = { ...axiosConfigHeaders };
    } else {
        headers = { ...axiosConfigHeaders, ...headers };
    }

    // Obtener baseURL desde las variables de entorno
    const baseURL = getBaseUrl(apiVersion);
    
    if (!baseURL) {
        throw new Error(`Versión de API inválida: ${apiVersion}`);
    }

    // Configuración de la solicitud Axios
    const config: AxiosRequestConfig = {
        method: method,
        url: url,            // Endpoint relativo
        baseURL,             // URL base desde .env
        data: data,
        headers: headers,
        params: params,
        cancelToken: source.token,
        responseType: responseType
    };

    const request = axios(config)
        .then((response: AxiosResponse) => response.data)
        .catch((error) => {
            if (axios.isCancel(error)) {
                console.error('Solicitud cancelada:', error.message);
                return undefined;
            }
            
            // Manejo de error 403: API key inválida
            if (error.response?.status === 403) {
                console.error('API Key inválida o expirada. Cerrando sesión...');
                localStorage.clear();
                window.location.href = '/';
                return;
            }
            
            // Mostrar alerta de error si está habilitado
            if (showAlert) {
                showApiError(url, null, error);
            }
            
            console.error('Error en la solicitud API:', error.message);
            throw error;
        });

    return {
        request,
        cancel: () => source.cancel('Operación cancelada por el usuario')
    };
}
